import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS type
import { IGDSPlanProps, scrollToRef } from '@redux/common';
import { RootState } from '@redux/store';

// import module
// component
// source config

// common function
import {
    convertTicketNum,
    decodeHTMLstring,
    addSeparator,
    getEarliestDate,
    getSessionInfo,
    getAllDatesInDetailData,
} from '@libs/core';
import { isMobile } from '@libs/globalVariables';
import { fetchGDSDetailData } from '@libs/apiCall';

// plugin
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

// redux actions
import {
    setTGCalenderErr,
    setTGSessionErr,
    setTGTicketErr,
    setTGSubmitErrMsg,
    setTGMInfoShow,
    setTGNowList,
    setTGNowListData,
    setCalendar,
    setTicketNum,
    setSession,
    setSessionPicked,
    setTotalAmount,
    setSalePriceSpread, 
    setShowCrossellTag,
    setTGDSDetailFetchFail,
    setGDetail,
    setGTempCalendarData,
    setIsTGDSDetailLoading,
    setIsTGDSSessionLoading,
    setTGDSSessionFetchFail,
    setScrollTo,
} from '@redux/GDST/actions';
import { setGDetailStore, setDetailFetchFailMsg } from '@redux/Common/actions';

const TGDSPlan: React.FC<IGDSPlanProps> = (props) => {
    // console.log('===================', props);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        FormatID,
        FormatName,
        LowListPrice,
        LowSalePrice,
        EarliestBuyDay,
        IntroductionArray,
        ExpectedUseDay,
    } = props.data;
    const { Currency, SaleCurrency, listIndex, isCrossell } = props;
    const {
        isTGDSDetailLoading,
        isTGDSSessionLoading,
        GDetail,
        calendar,
        ticketNum,
        TGNowList,
        TGNowListData,
        session,
        sessionPicked,
        TGCalenderErr,
        TGSessionErr,
        TGTicketErr,
        TGfinalAbleDates,
        TGSubmitErrMsg,
    } = useSelector((state: RootState) => state.GDST);
    const { ETID, Foreign, isLogin, GDetailStore } = useSelector((state: RootState) => state.Common);
    const { THSRSearchOption } = useSelector((state: RootState) => state.THSR);
    const hasCalendar = TGNowListData?.ExpectedUseDay;
    const hasSession = GDetail?.Sessions;

    const listBlockRef = useRef(null);

    useEffect(() => {
        if (!GDetail) return;
        // 更新ETITEM
        if (ExpectedUseDay) {
            const daysArrInMonth = getAllDatesInDetailData(GDetail, 'calendarDate');
            const firstDay = getEarliestDate(daysArrInMonth);
            if (!GDetail.Sessions) {
                daysArrInMonth.length !== 0
                    ? dispatch(setTicketNum(convertTicketNum(GDetail.CalendarDate?.[firstDay]?.ETItem)))
                    : dispatch(setTicketNum(convertTicketNum(GDetail.ETItem)))
            }
        } else {
            dispatch(setTicketNum(convertTicketNum(GDetail.ETItem)));
        }

        // 更新場次
        if (!GDetail.Sessions) {
            dispatch(setSession(null));
            dispatch(setSessionPicked(null));
        }
    }, [GDetail]);

    /**
     * 因目前GDS舊API是一次抓2個月，所以不會有跨月問題，之後改新API要新增跨月機制(參考自建)
     * TODO: 因高鐵選擇日期可能是跨月份，因此要判斷是否fetch跨月份資料
    */

    // 更新加總價格
    useEffect(() => {
        // 加總
        const calculateAmount = (clickedData: string) => {
            // const ETdata = ExpectedUseDay ? GDetail.CalendarDate[clickedData]?.ETItem : GDetail.ETItem;
            const ETdata = ExpectedUseDay
                ? hasSession
                    ? getSessionInfo({ session, sessionPicked })?.ETItem
                    : GDetail.CalendarDate[clickedData]?.ETItem
                : GDetail.ETItem;
            if (!ETdata) return 0;

            const hasSalePriceSpread = ETdata.some(item => item.SalePrice_Spread > 0);
            dispatch(setShowCrossellTag(hasSalePriceSpread));

            let totalAmount = 0;
            let totalSpread = 0;
            const keys = Object.keys(ticketNum);

            if(isLogin){
                keys.map((key) => {
                    let obj = ETdata.find((item) => item.ItemSeq === key);
                    if (!obj) return [];
                    if (obj) {
                        let price = obj.SalePrice !== 0 ? obj.SalePrice : obj.SalePrice_NonMember;
                        if (isCrossell) {
                            totalSpread += ticketNum[key] * obj.SalePrice_Spread;
                            price -= obj.SalePrice_Spread;
                        }
                        totalAmount += ticketNum[key] * price;
                    }
                });
            } else {
                keys.map((key) => {
                    let obj = ETdata.find((item) => item.ItemSeq === key);
                    if (obj) {
                        let price = obj.SalePrice_NonMember;
                        if (isCrossell) {
                            totalSpread += ticketNum[key] * obj.SalePrice_Spread;
                            price -= obj.SalePrice_Spread;
                        } else {
                        }
                        totalAmount += ticketNum[key] * price;
                    }
                });
            }
            dispatch(setSalePriceSpread(totalSpread));
            return totalAmount;
        };
        if (ticketNum) {
            const amount = calculateAmount(calendar);
            dispatch(setTotalAmount(addSeparator(amount)));
        } else {
            dispatch(setTotalAmount('0'));
        }
    }, [ticketNum, isCrossell]);


    // 清單牌價顯示規則
    const ShouldOriginPriceShow = (() => {
        if (LowListPrice === 0 || !LowListPrice) return false;
        switch (Foreign) {
            case '0':
                if (LowSalePrice >= LowListPrice) return false;
                break;
            case '1':
            default:
                return true;
        }
        return true;
    })();
    
    //選擇左側品項 
    const handleClickList = () => {
        if (TGNowList !== FormatID) {
            const fetchDetailProps = {
                ETID,
                EarliestBuyDay,
                ExpectedUseDay,
                FormatID,
                isTHSR: true,
                context: {
                    GDetailStore,
                    setGDetailStore,
                    setGDSDetailFetchFail: setTGDSDetailFetchFail,
                    setDetailFetchFailMsg,
                    setGDetail,
                    setGTempCalendarData,
                    setIsGDSDetailLoading: setIsTGDSDetailLoading,
                    setCalendar: setCalendar,
                    ThsrRoundType: THSRSearchOption.THSRType,
                    setGSessionLoading: setIsTGDSSessionLoading,
                    setGSessionFetchFail: setTGDSSessionFetchFail,
                    setSession,
                    setSessionPicked,
                },
                dispatch,
            };
            TGCalenderErr && dispatch(setTGCalenderErr(''));
            TGSessionErr && dispatch(setTGSessionErr(''));
            TGTicketErr && dispatch(setTGTicketErr(''));
            TGSubmitErrMsg && dispatch(setTGSubmitErrMsg(''));
            
            dispatch(setCalendar(getEarliestDate(TGfinalAbleDates)));
            fetchGDSDetailData(fetchDetailProps);
            dispatch(setTGNowListData(props.data));
            dispatch(setTGNowList(FormatID));
        }
    };

    const handleScrollTo = () => {
        if (!isMobile) return;
        if (hasCalendar) {
            dispatch(setScrollTo(scrollToRef.calender));
        } else {
            dispatch(setScrollTo(scrollToRef.ticket));
        }
    };

    return (
        <div
            className={cx('Tplan', 'gdsplanTHSR', {
                active: TGNowList === FormatID,
                disabled: ExpectedUseDay && !EarliestBuyDay,
            })}
            ref={listBlockRef}
            data-index={listIndex}
            onClick={() => {
                if (isTGDSDetailLoading || isTGDSSessionLoading || (ExpectedUseDay && !EarliestBuyDay)) return;
                handleClickList();
                handleScrollTo();
            }}
        >
            <div className="showInfo">
                <div className="leftPart">
                    <div className="title">{FormatName}</div>
                    {IntroductionArray && (
                        <div className="notice_wrap">
                            <ul className="notice">
                                {IntroductionArray?.map((notice, i) => {
                                    const htmlStr = decodeHTMLstring(notice);
                                    return (
                                        <li key={i}>
                                            <span
                                                dangerouslySetInnerHTML={{ __html: htmlStr }}
                                            ></span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="rightPart">
                    <div className="titleBlock">
                        <div className="title">{FormatName}</div>
                    </div>
                    <div className="pickBlock">
                        <div className="price">
                            {
                                (ExpectedUseDay && !EarliestBuyDay) 
                                ? 
                                <div className="soldOut">
                                    <span>{t('THSR.plan.soldOut')}</span>
                                </div>
                                :   
                                <>
                                    <div className="sale">
                                        <span className="saleCurrency">{SaleCurrency}</span>
                                        <span className="salePrice">{addSeparator(LowSalePrice)}</span>
                                        <span className="priceLabel">{t('THSR.plan.up')}</span>
                                    </div>
                                    {/* 高鐵面板一樣有原價顯示機制 */}
                                    {ShouldOriginPriceShow && (
                                        <div className="original">
                                            <span>{Currency}</span>
                                            <span>{addSeparator(LowListPrice)}</span>
                                        </div>
                                    )}
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="btn_note"
                onClick={(e) => {
                    e.stopPropagation();
                    handleClickList();
                    if (isMobile) dispatch(setTGMInfoShow(true));
                }}
            >
                <span>{t('THSR.plan.viewPlan')}</span>
            </div>
        </div>
    );
};
export default TGDSPlan;
